import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBars, FaBell, FaChevronDown, FaGlobe, FaSearch, FaTimes, FaUserTag } from 'react-icons/fa';
import { useAuth } from '../Context/AuthContext';
import { useNotification } from '../Context/NotificationContext';
import Notification from './Notification/Notification';
import { FEED_WORLD_SITE_URL, openFeedWorldWebsite } from '../apis/ssoApi';
import { setShellSearch, useShellSearch } from '../shared/shellSearch';
import { BrandName, brandName } from "../config/brand";

// The app's top bar (2026-10 dashboard design, styles in styles/feedShell.css): ☰ menu, FEED WORLD
// brand, search, the FEED WORLD website, the usertype, notifications and the profile menu.
const App = ({ showSidebarToggle, isSidebarExpanded, onToggleSidebar }) => {
  const { user, logout, userTypeContexts, activeContext, setActiveUserTypeCode, fullUserData } = useAuth();
  const { unreadCount, unreadNotifications, markNotificationAsRead, markAllNotificationsAsRead } = useNotification();
  const navigate = useNavigate();
  const search = useShellSearch();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isUsertypeSwitchOpen, setIsUsertypeSwitchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const profileRef = useRef(null);
  const usertypeSwitchRef = useRef(null);
  const notificationsRef = useRef(null);

  // Client-requested persistent header control (cross-service access plan, Part 5) - picks
  // which USERTYPE you're acting as (only interactive once a login genuinely holds more than
  // one - see AuthContext.userTypeContexts). Purely a display anchor: it scopes what Home shows
  // and which plan the subscription page reflects, never what's actually reachable by direct nav.
  const showUsertypeSwitcher = userTypeContexts.length > 1;
  // A single-usertype account still gets the same pill, just non-interactive (no dropdown, no
  // chevron) - so it can glance at the header and see what usertype it's acting as. Internal
  // accounts (userTypeEntity.internalUser) are excluded from the plain badge - their "usertype"
  // (Executive, etc.) isn't a service context the way an external account's is. The switcher above
  // stays keyed purely on context count regardless of internalUser: an internal account that
  // independently holds a second real entity (fpoEntity/companyEntity) still needs to switch to it.
  const isInternalUser = fullUserData?.userTypeEntity?.internalUser === true;
  const showUsertypeBadge = userTypeContexts.length === 1 && !isInternalUser;

  const handleSwitchUsertype = (context) => {
    setIsUsertypeSwitchOpen(false);
    setActiveUserTypeCode(context.userTypeCode);
    // Straight to that usertype's own home, not /home - HomeGateway always shows UsertypeGate
    // again for a 2+-context account, which would turn "switch to the other one" into "land back
    // on the exact same picker I could have clicked directly from".
    navigate(context.overviewRoute || "/home");
  };

  // Close the menus on outside click, same as a native <select>/menu would
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setIsProfileOpen(false);
      if (usertypeSwitchRef.current && !usertypeSwitchRef.current.contains(e.target)) setIsUsertypeSwitchOpen(false);
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) setIsNotificationsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Maps a notification's `type` (set server-side to the ResourceType value of the submission
  // it's about, e.g. ResourceType.CAPACITY_BUILDING.value()) to the page that shows it, so
  // clicking the notification takes the submitter straight to their request. Notification types
  // that aren't one of these (e.g. the generic "SystemNotification"/"SubscriptionNotification"
  // defaults) have no target and are left as a no-op.
  const NOTIFICATION_TYPE_ROUTES = {
    capacity_building: "capacity-building",
    fpo_business: "business-plan",
    legal_compliance: "compliances/legal",
    annual_compliance: "compliances/annual",
    agm_boards: "agm-board",
    // A path starting with "/" is a page of its own, not one inside the current service
    yuva_krushi: "/admin/yuva-krushi",
    safe_mission: "/admin/safe-mission",
    // News & Alerts: a published update, or a reminder for the member's FPO -- both on the dashboard
    news_alert: "/home",
    fpo_reminder: "/home",
    // "Post Your Suggestion" on the website's My Directory
    directory_suggestion: "/admin/directory-suggestions",
  };

  // Clicking a notification marks it read and opens a popup with its message -- navigation
  // to the relevant page happens only if the reader chooses the popup's View button.
  const handleNotificationClick = (notif, index) => {
    markNotificationAsRead(notif.id, index);
    setIsNotificationsOpen(false);
    setSelectedNotification(notif);
  };

  const closeNotificationPopup = () => setSelectedNotification(null);

  const handleNotificationView = () => {
    if (!selectedNotification) return;
    const targetPath = NOTIFICATION_TYPE_ROUTES[selectedNotification.type];
    // Read the service segment straight off the URL (the page the reader is on)
    const currentServiceKey = window.location.pathname.split('/').filter(Boolean)[0];
    if (targetPath && targetPath.startsWith('/')) {
      navigate(targetPath);
    } else if (targetPath && currentServiceKey) {
      navigate(`/${currentServiceKey}/${targetPath}`);
    }
    closeNotificationPopup();
  };

  const displayName = user?.displayName || 'My account';

  return (
    <header className="fw-topbar">
      {showSidebarToggle && (
        <button
          type="button"
          className="fw-icon-btn fw-menu-btn"
          aria-label={isSidebarExpanded ? "Collapse menu" : "Expand menu"}
          aria-expanded={isSidebarExpanded}
          onClick={onToggleSidebar}
        >
          <FaBars />
        </button>
      )}

      <Link to="/home" className="fw-brand" title={`${brandName()} home`}>
        <img src="/assets/img/logo/feedtablogo.webp" alt="" className="fw-brand-logo" width={42} height={42} />
        <span className="fw-brand-text">
          <BrandName />
          <small>कृषि एवं जयते</small>
        </span>
      </Link>

      {/* Narrows the dashboard's tiles, or the sidebar's items on other pages (shared/shellSearch.js);
          Enter opens the whole services catalog (/services) filtered by the same words */}
      <label className="fw-search" role="search">
        <FaSearch className="fw-search-icon" aria-hidden="true" />
        <input
          type="search"
          value={search}
          placeholder="Search services, products, markets..."
          aria-label="Search services, products and menus"
          onChange={(e) => setShellSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShellSearch('');
            if (e.key === 'Enter' && search.trim() && window.location.pathname !== '/services') navigate(`/services?q=${encodeURIComponent(search.trim())}`);
          }}
        />
        {search && (
          <button type="button" className="fw-search-clear" aria-label="Clear search" onClick={() => setShellSearch('')}>
            <FaTimes />
          </button>
        )}
      </label>

      <div className="fw-top-spacer" />

      {/* Back to the FEED WORLD website (the public site with Services, EPM, Know Your Schemes …) */}
      <a
        href={FEED_WORLD_SITE_URL}
        onClick={(e) => {
          // Signed in here: sign in on the website too (Ctrl / middle-click just opens it)
          if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          openFeedWorldWebsite();
        }}
        className="fw-top-action fw-website"
        title={`Open the ${brandName()} website`}
      >
        <FaGlobe aria-hidden="true" />
        <span>Website</span>
      </a>

      {showUsertypeSwitcher && (
        <div className="fw-menu-wrap" ref={usertypeSwitchRef}>
          <button
            type="button"
            className="fw-top-action fw-usertype"
            title="Switch usertype"
            aria-haspopup="menu"
            aria-expanded={isUsertypeSwitchOpen}
            onClick={() => setIsUsertypeSwitchOpen((open) => !open)}
          >
            <FaUserTag aria-hidden="true" />
            <span>{activeContext?.label}</span>
            <FaChevronDown className="fw-chev" aria-hidden="true" />
          </button>

          {isUsertypeSwitchOpen && (
            <ul className="fw-menu fw-usertype-menu" role="menu">
              <li className="fw-menu-heading">Switch Usertype</li>
              {userTypeContexts.map((context) => {
                const isCurrent = context.userTypeCode === activeContext?.userTypeCode;
                const isInstitutional = context.userTypeCode === 'INSTITUTIONAL';
                return (
                  <UsertypeSwitchRow
                    key={context.userTypeCode}
                    context={context}
                    isCurrent={isCurrent}
                    isInstitutional={isInstitutional}
                    onSelect={() => {
                      if (!isCurrent) handleSwitchUsertype(context);
                      else setIsUsertypeSwitchOpen(false);
                    }}
                  />
                );
              })}
            </ul>
          )}
        </div>
      )}

      {showUsertypeBadge && (
        <span className="fw-top-action fw-usertype is-static" title="Your usertype">
          <FaUserTag aria-hidden="true" />
          <span>{activeContext?.label}</span>
        </span>
      )}

      <div className="fw-menu-wrap" ref={notificationsRef}>
        <button
          type="button"
          className="fw-icon-btn fw-notify"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          aria-haspopup="menu"
          aria-expanded={isNotificationsOpen}
          onClick={() => setIsNotificationsOpen((open) => !open)}
        >
          <FaBell />
          {unreadCount > 0 && <span className="fw-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
        {isNotificationsOpen && (
          <div className="fw-menu fw-notify-menu" role="menu">
            {unreadNotifications.length === 0 ? (
              <div className="fw-menu-empty">No new notifications</div>
            ) : (
              <>
                {unreadCount > 0 && (
                  <div className="d-flex justify-content-end px-2 pb-1">
                    <button type="button" className="btn btn-link p-0" style={{ fontSize: 12 }} onClick={() => markAllNotificationsAsRead()}>
                      Mark all as read
                    </button>
                  </div>
                )}
                {unreadNotifications.map((notif, index) => (
                  <Notification
                    key={notif.id}
                    notification={notif}
                    isDropdownItem={true}
                    onClick={() => handleNotificationClick(notif, index)}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <div className="fw-menu-wrap" ref={profileRef}>
        <button
          type="button"
          className="fw-profile"
          aria-label={`${displayName} — profile menu`}
          aria-haspopup="menu"
          aria-expanded={isProfileOpen}
          onClick={() => setIsProfileOpen((open) => !open)}
        >
          <img className="fw-avatar" src="/assets/img/account/user.webp" alt="" />
          <span className="fw-profile-name">{displayName}</span>
          <FaChevronDown className="fw-chev" aria-hidden="true" />
        </button>
        {isProfileOpen && (
          <ul className="fw-menu fw-profile-menu" role="menu">
            <li className="fw-menu-heading fw-profile-heading">{displayName}</li>
            <li><a className="dropdown-item" href="#" onClick={e => { e.preventDefault(); setIsProfileOpen(false); navigate("/home/profile"); }}><i className="far fa-user" /> My Profile</a></li>
            <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); setIsProfileOpen(false); logout(); }}><i className="far fa-sign-out" /> Logout</a></li>
          </ul>
        )}
      </div>

      {/* Notification popup -- opened on click, shows the full message, with a View button
          that navigates to the relevant page (only when this notification's type maps to one). */}
      {selectedNotification && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
            <div className="modal-content" style={{ border: "none", borderRadius: 14, overflow: "hidden" }}>
              <div
                className="modal-header"
                style={{ background: "var(--theme-color)", border: "none", padding: "16px 22px" }}
              >
                <h5
                  className="modal-title text-white"
                  style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, fontSize: 17, marginBottom: 0 }}
                >
                  <i className="far fa-bell" />
                  {selectedNotification.title || "Notification"}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeNotificationPopup}></button>
              </div>
              {/* The theme's global .modal-body rule forces height: 70vh regardless of content
                  (public/assets/css/style.css:12197) -- override it explicitly so the popup
                  hugs its actual content instead of leaving a huge empty gap. */}
              <div className="modal-body" style={{ padding: "20px 22px", height: "auto", overflowY: "visible" }}>
                <div
                  style={{
                    background: "var(--theme-bg-light, #f8f9fa)",
                    border: "1px solid #e9ecef",
                    borderRadius: 10,
                    padding: "12px 14px",
                    fontSize: 14,
                    color: "#333",
                    whiteSpace: "pre-line",
                  }}
                >
                  {selectedNotification.message}
                </div>
              </div>
              <div className="modal-footer" style={{ border: "none", padding: "14px 22px" }}>
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-3"
                  onClick={closeNotificationPopup}
                >
                  Close
                </button>
                {NOTIFICATION_TYPE_ROUTES[selectedNotification.type] && (
                  <button
                    type="button"
                    className="btn btn-primary rounded-pill px-3"
                    onClick={handleNotificationView}
                  >
                    View
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// One row in the header's "Switch Usertype" dropdown. Split out as its own component (rather
// than inline JSX with onMouseEnter mutating currentTarget.style) so hover state is real React
// state - matches the same card-hover pattern used by UsertypeGate.jsx's identity cards, since
// this dropdown and that full-page gate are two views of the same "which usertype" concept and
// should read as the same visual language.
function UsertypeSwitchRow({ context, isCurrent, isInstitutional, onSelect }) {
  const [hover, setHover] = useState(false);

  return (
    <li style={{ marginBottom: 4 }}>
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); onSelect(); }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          padding: '12px', borderRadius: 12, textDecoration: 'none',
          background: isCurrent ? '#f0f7ec' : hover ? '#f7f9f8' : 'transparent',
          border: `1px solid ${isCurrent ? '#c8e6b8' : hover ? '#e4e9e5' : 'transparent'}`,
          boxShadow: isCurrent ? '0 4px 14px rgba(51,122,20,0.12)' : hover ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          transform: hover && !isCurrent ? 'translateX(2px)' : 'none',
          transition: 'all 0.15s ease',
        }}
      >
        <div
          style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: isCurrent ? '#337a14' : '#eef2f0',
            color: isCurrent ? '#fff' : '#337a14',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
            boxShadow: isCurrent ? '0 0 0 4px rgba(51,122,20,0.14)' : 'none',
            transition: 'box-shadow 0.15s ease',
          }}
        >
          <i className={`fas ${isInstitutional ? 'fa-building' : 'fa-user'}`} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 700,
              color: '#263238', fontFamily: 'Poppins, sans-serif',
            }}
          >
            {context.label}
            {isCurrent && <i className="fas fa-check-circle" style={{ color: '#337a14', fontSize: 12 }} />}
          </div>
          {(context.serviceLabel || context.plan) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
              {context.serviceLabel && (
                <span
                  style={{
                    fontSize: 10.5, fontWeight: 600, color: '#546e7a', background: '#f1f3f4',
                    padding: '3px 9px', borderRadius: 20,
                  }}
                >
                  {context.serviceLabel}
                </span>
              )}
              {context.plan?.name && (
                <span
                  style={{
                    fontSize: 10.5, fontWeight: 700, color: '#337a14', background: '#f0f7ec',
                    border: '1px solid #c8e6b8', padding: '3px 9px', borderRadius: 20,
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                  }}
                >
                  <i className="fas fa-crown" style={{ fontSize: 8 }} />
                  {context.plan.name}
                </span>
              )}
            </div>
          )}
        </div>
        {!isCurrent && (
          <i
            className="far fa-arrow-right"
            style={{ fontSize: 12, color: hover ? '#337a14' : '#c4cbc5', transition: 'color 0.15s ease' }}
          />
        )}
      </a>
    </li>
  );
}

export default App;
